import { BarChart3 } from "lucide-react";

import type {
  ITotaisCobrancaCreditoCliente,
  ITotalConsumoPorProduto,
} from "@/interface/extrato-credito-cliente";

type ResumoConsumoCreditoProps = {
  totais: Partial<ITotaisCobrancaCreditoCliente> | undefined;
  totaisPorProduto: ITotalConsumoPorProduto[];
  carregando: boolean;
  resumoPeriodo: string;
  /** Exibe o card "Valor total da cobrança". Desligado na visão do próprio cliente. */
  exibirValorTotal?: boolean;
};

function formatarQuantidadeCredito(valor: number): string {
  return Number.isInteger(valor) ? String(valor) : valor.toFixed(2);
}

function formatarMoeda(valor: number): string {
  return valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function ResumoConsumoCredito({
  totais,
  totaisPorProduto,
  carregando,
  resumoPeriodo,
  exibirValorTotal = true,
}: ResumoConsumoCreditoProps) {
  const totalCreditos = totais?.total_creditos_consumidos ?? 0;
  const totalRemessas = totais?.total_remessas_faturaveis ?? 0;
  const valorTotalCobranca = totais?.valor_total_cobranca ?? 0;

  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
      <div className="mb-3 flex items-center gap-2">
        <BarChart3 className="h-4 w-4 text-neutral-600" aria-hidden />
        <h3 className="text-sm font-semibold text-neutral-900">Consumo faturável no período</h3>
        <span className="text-xs text-muted-foreground">({resumoPeriodo})</span>
      </div>

      {carregando ? (
        <p className="text-sm text-muted-foreground">Carregando resumo...</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="min-w-[140px] rounded-md border border-neutral-200 bg-white px-3 py-2">
              <p className="text-xs text-muted-foreground">Total de créditos</p>
              <p className="text-lg font-semibold text-neutral-900">
                {formatarQuantidadeCredito(totalCreditos)}
              </p>
            </div>
            <div className="min-w-[140px] rounded-md border border-neutral-200 bg-white px-3 py-2">
              <p className="text-xs text-muted-foreground">Remessas faturáveis</p>
              <p className="text-lg font-semibold text-neutral-900">{totalRemessas}</p>
            </div>
            {exibirValorTotal && (
              <div className="min-w-[140px] rounded-md border border-neutral-200 bg-white px-3 py-2">
                <p className="text-xs text-muted-foreground">Valor total da cobrança</p>
                <p className="text-lg font-semibold text-neutral-900">
                  {formatarMoeda(valorTotalCobranca)}
                </p>
              </div>
            )}
          </div>

          {totaisPorProduto.length > 0 ? (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-600">
                Por produto
              </p>
              <div className="flex flex-wrap gap-2">
                {totaisPorProduto.map((item) => (
                  <div
                    key={item.produto_id}
                    className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-sm"
                  >
                    <span className="font-medium text-neutral-800">{item.produto_nome}</span>
                    <span className="text-muted-foreground">
                      {formatarQuantidadeCredito(item.total_creditos_consumidos)} créd.
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Nenhum consumo faturável encontrado para os filtros selecionados.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
